const STORAGE_KEY = 'ably.auth.last-login-method';
const VALID_METHODS = ['github', 'google', 'email', 'sso'] as const;

type LoginMethod = (typeof VALID_METHODS)[number];

const isLoginMethod = (value: string | null | undefined): value is LoginMethod =>
  value != null && (VALID_METHODS as readonly string[]).includes(value);

const readLastMethod = (): LoginMethod | null => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return isLoginMethod(stored) ? stored : null;
  } catch {
    // localStorage can throw in private mode or when blocked — the badge is a nicety, so ignore.
    return null;
  }
};

const rememberMethod = (method: LoginMethod) => {
  try {
    localStorage.setItem(STORAGE_KEY, method);
  } catch {
    // Non-critical: failing to remember just means no badge next time.
  }
};

// Records the chosen method on click and reveals the "Last" badge for the
// previously-used one. Recording on click (rather than on verified success) is
// intentional — it's front-end only and self-corrects on the next login.
const setupLastLoginMethod = () => {
  document.querySelectorAll<HTMLElement>('[data-login-method]').forEach((el) => {
    el.addEventListener('click', () => {
      const method = el.dataset.loginMethod;
      if (isLoginMethod(method)) {
        rememberMethod(method);
      }
    });
  });

  const lastMethod = readLastMethod();
  if (!lastMethod) {
    return;
  }

  const badge = document.querySelector<HTMLElement>(`[data-last-login-badge="${lastMethod}"]`);
  if (!badge) {
    return;
  }
  badge.classList.remove('hidden');

  // SSO's badge lives on the button inside the collapsed email accordion, so
  // expand it — otherwise the badge is hidden on load. (The email badge sits on
  // the always-visible toggle, so it isn't inside the form and won't trip this.)
  const emailForm = document.getElementById('new_session');
  const emailToggle = document.getElementById('continue-with-email');
  if (emailForm?.contains(badge) && emailForm.style.maxHeight === '0px') {
    emailToggle?.click();
  }
};

export {
  setupLastLoginMethod,
  STORAGE_KEY,
  readLastMethod as readLastLoginMethod,
  rememberMethod as rememberLoginMethod,
};
export type { LoginMethod };
